import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  Banknote,
  CheckCircle,
  CreditCard,
  Printer,
  Smartphone,
  Tag,
} from 'lucide-react'
import { useRef, useState } from 'react'
import { useReactToPrint } from 'react-to-print'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCompanyMutation } from '@/hooks/useCompanyMutation'
import { supabase } from '@/integrations/supabase/client'
import { logAuditAction } from '@/lib/audit'
import { useCompany } from '@/lib/CompanyProvider'
import type { Order } from './KanbanCard'
import { ThermalReceipt } from './ThermalReceipt'

interface CheckoutModalProps {
  order: Order | null
  isOpen: boolean
  onClose: () => void
}

export function CheckoutModal({ order, isOpen, onClose }: CheckoutModalProps) {
  const queryClient = useQueryClient()
  const { activeCompanyId } = useCompany()
  const [method, setMethod] = useState<string>('PIX')
  const [cashGiven, setCashGiven] = useState<string>('')

  // Buscar Caixa Aberto
  const { data: openRegister } = useQuery({
    queryKey: ['open_register', activeCompanyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenant_cash_registers')
        .select('id')
        .eq('company_id', activeCompanyId)
        .eq('status', 'OPEN')
        .order('opened_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      if (error) throw error
      return data || null
    },
    enabled: !!activeCompanyId,
  })

  // -- Coupon State --
  const [couponInput, setCouponInput] = useState('')
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null)
  const [discountAmount, setDiscountAmount] = useState(0)
  const [couponError, setCouponError] = useState('')
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false)

  const subtotal = order?.total || 0
  const finalTotal = Math.max(0, subtotal - discountAmount)
  const numericCash = parseFloat(cashGiven) || 0
  const change = numericCash - finalTotal

  const { data: company } = useQuery({
    queryKey: ['company_settings', activeCompanyId],
    queryFn: async () => {
      if (!activeCompanyId) return null
      const { data } = await supabase
        .from('company_settings')
        .select('*')
        .eq('id', activeCompanyId)
        .single()
      return data
    },
    enabled: !!activeCompanyId,
  })

  const receiptRef = useRef<HTMLDivElement>(null)
  const handlePrint = useReactToPrint({
    contentRef: receiptRef,
    documentTitle: `Recibo_OS${order?.os_number ? String(order.os_number).padStart(3, '0') : order?.id?.substring(0, 8)}`,
  })

  const checkoutMutation = useCompanyMutation({
    mutationFn: async ({
      orderId,
      paymentMethod,
    }: {
      orderId: string
      paymentMethod: string
      company_id?: string
    }) => {
      if (!activeCompanyId) throw new Error('Empresa no selecionada')
      if (!order) throw new Error('OS invlida')

      // 1. Atualizar Ordem de Serviço
      const { error: orderError } = await supabase
        .from('orders')
        .update({
          status: 'PAGO',
          payment_method: paymentMethod,
          paid_at: new Date().toISOString(),
          coupon_code: appliedCoupon ? appliedCoupon.code : null,
          discount_amount: discountAmount,
          total: finalTotal, // V2 update the final total to reflect reality
        })
        .eq('id', orderId)
        .eq('company_id', activeCompanyId)

      if (orderError) throw orderError

      // 2. Inserir Transação Financeira (Entrada)
      const { error: txError } = await supabase.from('cash_transactions').insert({
        id: crypto.randomUUID(),
        company_id: activeCompanyId,
        os_id: orderId,
        register_id: openRegister?.id || null,
        type: 'ENTRADA',
        amount: finalTotal,
        method: paymentMethod,
        description: `Pagamento OS ${order.plate || orderId} (${order.vehicle_model || 'Veículo'})`,
      })

      if (txError) throw txError

      // 3. Gerar Recibo de Comissão no Ledger (Se houver responsável)
      if (order.assigned_to) {
        const { data: collabData, error: collabErr } = await supabase
          .from('collaborators')
          .select('commission_rate')
          .eq('id', order.assigned_to)
          .single()

        if (!collabErr && collabData) {
          const rate = Number(collabData.commission_rate) || 0
          const commissionAmount = (finalTotal * rate) / 100

          if (commissionAmount > 0) {
            const { error: ledgerErr } = await supabase.from('commissions_ledger').insert({
              id: crypto.randomUUID(),
              company_id: activeCompanyId,
              collaborator_id: order.assigned_to,
              os_id: orderId,
              amount: commissionAmount,
              status: 'PENDENTE',
            })

            if (ledgerErr) {
              console.error('Erro ao gerar comissão no ledger:', ledgerErr)
            }
          }
        }
      }

      // 4. Registrar na Auditoria
      await logAuditAction({
        companyId: activeCompanyId,
        type: 'CASH_TRANSACTION',
        details: `OS ${order.os_number ? `#${String(order.os_number).padStart(3, '0')}` : orderId.substring(0, 8)} finalizada via ${paymentMethod} no valor de R$ ${finalTotal.toFixed(2)}${appliedCoupon ? ` (Cupom: ${appliedCoupon.code})` : ''}`,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['cash_transactions'] })
      onClose()
    },
    onError: (err: any) => {
      alert(`Erro ao finalizar OS: ${err.message}`)
    },
  })

  if (!order) return null

  const handleApplyCoupon = async () => {
    setCouponError('')
    setAppliedCoupon(null)
    setDiscountAmount(0)

    if (!couponInput) return
    setIsApplyingCoupon(true)

    try {
      const code = couponInput.trim().toUpperCase()
      const { data, error } = await supabase
        .from('tenant_coupons')
        .select('*')
        .eq('company_id', activeCompanyId)
        .eq('code', code)
        .maybeSingle()

      if (error || !data || data.status !== 'ATIVO') {
        setCouponError('Cupom inválido ou inativo.')
        return
      }

      const coupon = data
      let baseValue = subtotal

      if (coupon.restricted_service) {
        let services: any[] = []
        try {
          services =
            typeof order?.services === 'string' ? JSON.parse(order.services) : order?.services || []
        } catch {
          // ignore error
        }

        const matchedSvc = services.find((s: any) => s.name === coupon.restricted_service)
        if (!matchedSvc) {
          setCouponError(`Válido apenas para: ${coupon.restricted_service}`)
          return
        }
        baseValue = Number(matchedSvc.price) || 0
      }

      let calculatedDiscount =
        coupon.type === 'PERCENT' ? (baseValue * Number(coupon.value)) / 100 : Number(coupon.value)
      calculatedDiscount = Math.min(subtotal, calculatedDiscount)

      setDiscountAmount(calculatedDiscount)
      setAppliedCoupon(coupon)
    } catch {
      setCouponError('Erro ao validar cupom.')
    } finally {
      setIsApplyingCoupon(false)
    }
  }

  const handleConfirm = () => {
    if (method === 'Dinheiro' && numericCash < finalTotal) {
      alert('O valor recebido não pode ser menor que o total a pagar.')
      return
    }
    checkoutMutation.mutate({ orderId: order.id, paymentMethod: method })
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px] bg-slate-900 border-slate-700 text-slate-100 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <CheckCircle className="text-emerald-500" /> Finalizar OS{' '}
            {order.os_number ? `#${String(order.os_number).padStart(3, '0')}` : ''}
          </DialogTitle>
        </DialogHeader>

        {!openRegister && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-amber-500 text-sm flex gap-2">
            <AlertTriangle size={18} className="shrink-0" />
            <p>
              <strong>Atenção:</strong> O caixa está fechado. Abra uma sessão de caixa na aba
              Financeiro para receber pagamentos.
            </p>
          </div>
        )}

        <div className="flex flex-col gap-4 py-2">
          <div className="bg-slate-800 p-3 rounded-lg border border-slate-700">
            <div className="flex justify-between items-center mb-2">
              <span className="text-slate-400">Veículo:</span>
              <span className="font-semibold">
                {order.vehicle_model} ({order.plate})
              </span>
            </div>
            <div className="flex justify-between items-center mb-4">
              <span className="text-slate-400">Cliente:</span>
              <span className="font-semibold">{order.customer_name}</span>
            </div>
            <div className="flex justify-between items-center mb-4 border-t border-slate-700 pt-3">
              <span className="text-slate-400">Subtotal:</span>
              <span className="font-semibold">R$ {subtotal.toFixed(2)}</span>
            </div>

            {/* Sistema de Cupons */}
            <div className="mb-4">
              <label className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-1">
                <Tag size={14} /> Cupom de Desconto
              </label>
              <div className="flex gap-2">
                <Input
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                  placeholder="EX: PROMO10"
                  className="bg-slate-950 border-slate-700 uppercase"
                  disabled={!!appliedCoupon || isApplyingCoupon}
                />
                {!appliedCoupon ? (
                  <Button
                    onClick={handleApplyCoupon}
                    disabled={isApplyingCoupon || !couponInput}
                    className="bg-slate-700 hover:bg-slate-600"
                  >
                    Aplicar
                  </Button>
                ) : (
                  <Button
                    onClick={() => {
                      setAppliedCoupon(null)
                      setDiscountAmount(0)
                      setCouponInput('')
                    }}
                    variant="destructive"
                  >
                    Remover
                  </Button>
                )}
              </div>
              {couponError && <p className="text-xs text-rose-400 mt-1">{couponError}</p>}
              {appliedCoupon && (
                <div className="flex justify-between items-center mt-2 text-emerald-400 text-sm font-semibold">
                  <span>(-) Desconto ({appliedCoupon.code})</span>
                  <span>- R$ {discountAmount.toFixed(2)}</span>
                </div>
              )}
            </div>

            <div className="pt-3 border-t border-slate-700 flex justify-between items-center">
              <span className="text-slate-300 font-medium">Total a Receber</span>
              <span className="text-2xl font-bold text-emerald-400">
                R$ {finalTotal.toFixed(2)}
              </span>
            </div>
          </div>

          <div className="space-y-3">
            <label className="text-sm font-medium text-slate-300">Forma de Pagamento</label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="w-full bg-slate-950 border-slate-700 text-slate-100 h-12">
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700 text-slate-100">
                <SelectItem value="PIX">
                  <div className="flex items-center gap-2">
                    <Smartphone size={16} /> PIX (Instantâneo)
                  </div>
                </SelectItem>
                <SelectItem value="Cartão de Crédito">
                  <div className="flex items-center gap-2">
                    <CreditCard size={16} /> Cartão de Crédito
                  </div>
                </SelectItem>
                <SelectItem value="Cartão de Débito">
                  <div className="flex items-center gap-2">
                    <CreditCard size={16} /> Cartão de Débito
                  </div>
                </SelectItem>
                <SelectItem value="Dinheiro">
                  <div className="flex items-center gap-2">
                    <Banknote size={16} /> Dinheiro
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {method === 'Dinheiro' && (
            <div className="space-y-4 bg-slate-950 p-4 rounded-lg border border-slate-700">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300">Valor Entregue (R$)</label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={cashGiven}
                  onChange={(e) => setCashGiven(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-lg font-bold text-white h-12"
                />
              </div>
              <div
                className={`p-3 rounded-md text-center border font-bold text-lg ${
                  change < 0
                    ? 'bg-red-500/10 text-red-400 border-red-500/30'
                    : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                }`}
              >
                {change < 0
                  ? `Falta: R$ ${Math.abs(change).toFixed(2)}`
                  : `Troco a Devolver: R$ ${change.toFixed(2)}`}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="sm:justify-between mt-4">
          <Button
            variant="outline"
            onClick={() => handlePrint()}
            className="border-blue-700 bg-blue-900/20 text-blue-400 hover:bg-blue-800 hover:text-white"
          >
            <Printer size={16} className="mr-2" /> Imprimir Recibo
          </Button>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 hover:text-white"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={
                checkoutMutation.isPending || (method === 'Dinheiro' && change < 0) || !openRegister
              }
              className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
            >
              {checkoutMutation.isPending ? 'Processando...' : 'Confirmar Recebimento'}
            </Button>
          </div>
        </DialogFooter>

        {/* Hidden Thermal Receipt for Print */}
        {order && (
          <ThermalReceipt
            ref={receiptRef}
            order={order}
            company={company}
            customer={{ name: order.customer_name }}
            vehicle={{ plate: order.plate, model: order.vehicle_model }}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
